import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { OccurrenceStepEnum, OccurrenceStepLabelEnum, getOccurrenceStepLabelByStepValue } from '../../../enums/occurrenceStep';
import {
  Occurrence,
  OccurrenceDispatch,
  OccurrenceReview,
  OccurrenceStep,
} from '../../../models/occurrence';
import { requiredFieldsByStep } from '../components/form/utils/required-fields-by-step';

import { useSelector } from 'react-redux';
import ConfirmDialog from '../../../components/dialogs/ConfirmDialog';
import { getUserProfile } from '../../../configuration/login/loginSlice';
import BudgetsForm from '../../budgetsForm';
import AnalysisWarrantyForm from '../components/form/analysisWarrantyForm';
import AssessmentForm from '../components/form/assessmentForm';
import AwaitingRepairForm from '../components/form/awaitingRepairForm';
import CallForm from '../components/form/callForm';
import ChangeStepForm from '../components/form/changeStepForm';
import CustomerOkForm from '../components/form/customerOkForm';
import DisplacementForm from '../components/form/displacementForm';
import FourthDiagnosisForm from '../components/form/fourthDiagnosisForm';
import PartsForm from '../components/form/partsForm';
import PartsInTransitForm from '../components/form/partsInTransitForm';
import ReleaseForm from '../components/form/releaseForm';
import RemovalForm from '../components/form/removalForm';
import RepairForm from '../components/form/repairForm';
import ScreeningForm from '../components/form/screeningForm';
import SecondDiagnosisForm from '../components/form/secondDiagnosisForm';
import ThirdDiagnosisForm from '../components/form/thirdDiagnosisForm';
import Worklog from '../components/worklog';
import SupportTab from '../components/form/components/supportTab';

import { Scheduling } from '../../../models/schedule';

import { fetchSchedulingByChassisList } from '../../../api/fetchScheduling';
import { fetchUser, fetchUserRole, IUserProfile } from '../../../api/fetchUser';
import { fetchOccurrence } from '../../../api/fetchOccurence';
import { UserRoleEnum } from '../../../enums/role';

import { useFeatureFlag } from '../../../hooks/useFeatureFlag';

import '../styles.scss';

interface IFormDialogProps {
  showDialog: boolean;
  reloadOccurrenceList: (occurrenceIdToRemove?: string, occurrenceIdToAdd?: string) => Promise<void>
  onClose: () => void;
  occurrence: Occurrence | null;
  partners?: { id: string; name: string }[];
  summaryStep?: OccurrenceStep | null;
  step: OccurrenceStepEnum;
  isValidStep: (requiredFields: string[], occurrence: Occurrence) => boolean;
  loading: boolean;
  save: (occurrence: Occurrence) => Promise<Occurrence | false>;
  disabledAllFields?: boolean;
  changeStep: (
    service: Occurrence,
    fromStepId: number,
    to: OccurrenceStepEnum,
  ) => Promise<Occurrence | null | undefined>;
  view: 'form' | 'changeStep' | 'comments' | 'worklog' | 'budgets' | 'support';
  setView: (view: 'form' | 'changeStep' | 'comments' | 'worklog' | 'budgets' | 'support') => void;
  getNotFilledRequiredFields: (requiredFields: string[], occurrence: Occurrence) => string[];
  finishOccurrence: (
    occurrence: Occurrence,
    finalizationReasonType?: string,
    finalizationReasonDescription?: string,
    fromStepId?: number,
  ) => Promise<boolean>;
  persona: 'MANAGER' | 'TOWER' | 'CONSULTANT' | 'PARTNERSHIP';
  finalized?: boolean;
  review: OccurrenceReview[];
  reviewStep: (
    occurrenceId: string,
    step: OccurrenceStepEnum,
    rate: number,
    comment?: string,
  ) => Promise<void>;
  occurrenceStepAvailable: string[];
  upsertDispatch?: (
    occurrence: Occurrence,
    dispatch: OccurrenceDispatch,
  ) => Promise<{ wasCreated: boolean; error?: string; dispatch?: OccurrenceDispatch }>;
  cancelDispatch?: (
    occurrence: Occurrence,
    dispatch: OccurrenceDispatch,
    reason: string,
    description: string,
  ) => Promise<{ wasCanceled: boolean; error?: string; dispatch?: OccurrenceDispatch }>;
  removeDispatch?: (
    occurrence: Occurrence,
    dispatch: OccurrenceDispatch,
  ) => Promise<void>;
}

const StepFormDialog = (props: IFormDialogProps) => {
  const intl = useIntl();
  const [occurrenceInit, setOccurrenceInit] = useState<Occurrence | null>(props.occurrence);
  const [occurrenceFormHasChanged, setOccurrenceFormHasChanged] = useState<boolean>(false);
  const [occurrence, setOccurrence] = useState<Occurrence | null>(props.occurrence);
  const [scheduling, setScheduling] = useState<Scheduling | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const userProfile = useSelector(getUserProfile);
  const [user, setUser] = useState<IUserProfile | undefined>(undefined);
  const [userRole, setUserRole] = useState<UserRoleEnum | undefined>(undefined);
  const reloadOccurrenceList = props.reloadOccurrenceList;

  // Feature flag para exibir ou não a aba de orçamentos
  const showBudget = useFeatureFlag('budget');
  const showBudgetConsultant = useFeatureFlag('budget.consultant');

  useEffect(() => {
    const fetchStep = async () => {
      const occurrence = props.occurrence;

      setOccurrence(
        occurrence
          ? { ...occurrence, accountName: props.occurrence?.accountName }
          : ({} as Occurrence),
      );
      setOccurrenceInit(
        occurrence
          ? { ...occurrence, accountName: props.occurrence?.accountName }
          : ({} as Occurrence),
      );

      if (occurrence?.scheduleNumber) {
        const scheduling = (await fetchSchedulingByChassisList([occurrence.vehicleChassis])).find(
          (e) => e.scheduleNumber === occurrence.scheduleNumber,
        );
        setScheduling(scheduling);
      }

      if (occurrence?.id) {
        const freshOccurrence = await fetchOccurrence(occurrence.id, props.persona, props.finalized);
        if (freshOccurrence?.lastWorklog) {
          setOccurrence((prev) => (prev ? { ...prev, lastWorklog: freshOccurrence.lastWorklog } : prev));
          setOccurrenceInit((prev) => (prev ? { ...prev, lastWorklog: freshOccurrence.lastWorklog } : prev));
        }
      }

      const userId = userProfile?.sub?.split(':')[1];
      if (userId) setUser(await fetchUser(userId));

      console.log('[StepFormDialog] Buscando perfil do usuário...');
      try {
        const role = await fetchUserRole();
        console.log('[StepFormDialog] Resposta do endpoint /users/role:', role);
        if (role) {
          setUserRole(role);
          console.log('[StepFormDialog] Perfil do usuário definido:', role);
        } else {
          console.warn('[StepFormDialog] Perfil do usuário não retornado pelo endpoint');
        }
      } catch (error) {
        console.error('[StepFormDialog] Erro ao buscar perfil do usuário:', error);
      }

      if (occurrence?.id) {
        try {
          const fullOccurrence = await fetchOccurrence(occurrence.id, props.persona, props.finalized);
          if (fullOccurrence) {
            const merged = { ...fullOccurrence, accountName: fullOccurrence.accountName ?? occurrence.accountName };
            setOccurrence(merged);
            setOccurrenceInit(merged);
          }
        } catch (error) {
          console.error('[StepFormDialog] Erro ao buscar dados completos da ocorrência:', error);
        }
      }
    };
    if (props.showDialog) {
      console.log('[StepFormDialog] Diálogo aberto, iniciando busca de dados...');
      fetchStep();
    } else {
      setOccurrenceFormHasChanged(false);
    }
  }, [props.step, props.showDialog, userProfile, props.occurrence?.steps]);

  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);

  const formProps = {
    occurrence: occurrence,
    summaryStep: props.summaryStep,
    partners: props.partners,
    setOccurrence: (updatedOccurrence: Partial<Occurrence>) => {
      setOccurrenceFormHasChanged(true);
      setOccurrence({ ...occurrence, ...updatedOccurrence } as Occurrence);
    },
    setOccurrenceValue: (attr: string, value: any) => {
      if(attr !== "lastWorklog") {
        setOccurrenceFormHasChanged(true);
      }
      setOccurrence((prev) => ({ ...prev, [attr]: value } as Occurrence));
    },
    setOccurrenceStepValue: (step: OccurrenceStepLabelEnum, attr: string, value: any) => {
      setOccurrenceFormHasChanged(true);
      setOccurrence({
        ...occurrence,
        steps:
          occurrence?.steps?.map((stepEl) =>
            stepEl.name === step ? { ...stepEl, [attr]: value } : stepEl,
          ) ?? [],
      } as Occurrence);
    },
    step: occurrence?.steps?.find((s) => s.step === props.step) ?? ({} as OccurrenceStep),
    isValidStep: (requiredFields: string[]) =>
      props.isValidStep(requiredFields, occurrence ?? ({} as Occurrence)),
    loading,
    save: async (occurrence: Occurrence) => {
      setLoading(true);
      const result = await props.save(occurrence);
      if (result) {
        const updatedOccurrence = {
          ...occurrence,
          version: result.version,
        };
        setOccurrenceInit(updatedOccurrence);
        const updatedByName = (user?.firstName && user?.lastName)
          ? `${user.firstName} ${user.lastName}`
          : "Você";
        setOccurrence({
          ...updatedOccurrence,
          steps: occurrence.steps?.map((step) => {
            if (step.step === props.step) {
              return {
                ...step,
                updatedAt: new Date(),
                updatedBy: updatedByName,
              }
            }
            return step;
          }),
        });
        setOccurrenceFormHasChanged(false);
      }
      setLoading(false);
      return result;
    },
    onClose: props.onClose,
    disabledAllFields: props.disabledAllFields,
    persona: props.persona,
    finalized: props.finalized,
    upsertDispatch: props.upsertDispatch,
    cancelDispatch: props.cancelDispatch,
    removeDispatch: props.removeDispatch,
    getNotFilledRequiredFields: props.getNotFilledRequiredFields,
    finishOccurrence: props.finishOccurrence,
    changeStep: props.changeStep,
    scheduling: scheduling,
  };

  const form = () => {
    switch (props.step) {
      case OccurrenceStepEnum.CALL:
        return <CallForm {...formProps} />;

      case OccurrenceStepEnum.SCREENING:
        return <ScreeningForm {...formProps} />;

      case OccurrenceStepEnum.DISPLACEMENT:
        return <DisplacementForm {...formProps} />;

      case OccurrenceStepEnum.SECOND_DIAGNOSIS:
        return <SecondDiagnosisForm {...formProps} />;

      case OccurrenceStepEnum.REMOVAL:
        return <RemovalForm {...formProps} />;

      case OccurrenceStepEnum.THIRD_DIAGNOSIS:
        return <ThirdDiagnosisForm {...formProps} />;

      case OccurrenceStepEnum.FOURTH_DIAGNOSIS:
        return <FourthDiagnosisForm {...formProps} />;

      case OccurrenceStepEnum.ANALYSIS_WARRANTY:
        return <AnalysisWarrantyForm {...formProps} />;

      case OccurrenceStepEnum.CUSTOMER_OK:
        return <CustomerOkForm {...formProps} />;

      case OccurrenceStepEnum.PARTS:
        return <PartsForm {...formProps} />;

      case OccurrenceStepEnum.PARTS_IN_TRANSIT:
        return <PartsInTransitForm {...formProps} />;

      case OccurrenceStepEnum.AWAITING_REPAIR:
        return <AwaitingRepairForm {...formProps} />;

      case OccurrenceStepEnum.REPAIR:
        return <RepairForm {...formProps} />;

      case OccurrenceStepEnum.RELEASE:
        return <ReleaseForm {...formProps} />;

      default:
        return null;
    }
  };

  const matchedStep = occurrence?.steps?.find((s) => s.step === props.step);
  const resolvedStep: OccurrenceStep =
    matchedStep ??
    props.summaryStep ??
    ({
      step: props.step,
      name: getOccurrenceStepLabelByStepValue(props.step) as OccurrenceStepLabelEnum,
      latest: 0,
    } as OccurrenceStep);

  const changeStepProps = {
    step: resolvedStep,
    occurrence: occurrence ?? null,
    changeStep: async (occurrence: Occurrence, fromStepId: number, to: OccurrenceStepEnum) => {
      const result = await props.changeStep(occurrence, fromStepId, to);
      if (!result) return result;
      setOccurrence(result);
      return result;
    },
    onClose: props.onClose,
    save: formProps.save,
    requiredFields: [] as string[],
    isValidStep: (requiredFields: string[]) =>
      props.isValidStep(requiredFields, occurrence ?? ({} as Occurrence)),
    hasUnsavedChanges: () => occurrenceFormHasChanged,
    getNotFilledRequiredFields: (requiredFields: string[], occurrence: Occurrence) =>
      props.getNotFilledRequiredFields(requiredFields, occurrence),
    finishOccurrence: async (
      occurrence: Occurrence,
      finalizationReasonType?: string,
      finalizationReasonDescription?: string,
      fromStepId?: number,
    ) => {
      const result = await props.finishOccurrence(
        occurrence,
        finalizationReasonType,
        finalizationReasonDescription,
        fromStepId,
      );
      return result;
    },
    occurrenceStepAvailable: props.occurrenceStepAvailable,
  };

  const changeStep = () => {
    let props = changeStepProps;
    const isPreventive = !!occurrence?.scheduleNumber;
    const occurrenceType = occurrence?.occurrenceType;
    const isRoadAssistance = occurrenceType === 'A' || occurrenceType === 'W';
    const isDealership = occurrenceType === 'D';

    switch (props.step.step) {
      case OccurrenceStepEnum.CALL:
        if (isPreventive) {
          props.requiredFields = requiredFieldsByStep.CALL_PREVENTIVE_CHECKUP;
        } else if (isDealership) {
          const dealershipAllowed = [
            'vehicleOdometer', 'vehicleLicensePlate', 'vehicleModel',
            'dealershipDn', 'emissionStandard', 'country', 'source', 'occurrenceType',
          ];
          props.requiredFields = requiredFieldsByStep.CALL.filter(f => dealershipAllowed.includes(f));
        } else {
          props.requiredFields = requiredFieldsByStep.CALL.filter((f) => {
            if (isRoadAssistance && f === 'driverName') return false;
            if (f === 'vehicleStopped') return false;
            return true;
          });
        }
        break;

      case OccurrenceStepEnum.SCREENING:
        props.requiredFields = isPreventive
          ? requiredFieldsByStep.SCREENING_PREVENTIVE_CHECKUP
          : requiredFieldsByStep.SCREENING;
        break;

      case OccurrenceStepEnum.DISPLACEMENT:
        props.requiredFields = isPreventive
          ? requiredFieldsByStep.DISPLACEMENT_PREVENTIVE_CHECKUP
          : requiredFieldsByStep.DISPLACEMENT;
        break;

      case OccurrenceStepEnum.SECOND_DIAGNOSIS:
        props.requiredFields = isPreventive
          ? requiredFieldsByStep.SECOND_DIAGNOSIS_PREVENTIVE_CHECKUP
          : requiredFieldsByStep.SECOND_DIAGNOSIS;
        break;

      case OccurrenceStepEnum.REMOVAL:
        props.requiredFields = isPreventive
          ? requiredFieldsByStep.REMOVAL_PREVENTIVE_CHECKUP
          : requiredFieldsByStep.REMOVAL;
        break;

      case OccurrenceStepEnum.THIRD_DIAGNOSIS:
        props.requiredFields = isPreventive
          ? requiredFieldsByStep.THIRD_DIAGNOSIS_PREVENTIVE_CHECKUP
          : requiredFieldsByStep.THIRD_DIAGNOSIS;
        break;

      case OccurrenceStepEnum.FOURTH_DIAGNOSIS:
        props.requiredFields = isPreventive
          ? requiredFieldsByStep.FOURTH_DIAGNOSIS_PREVENTIVE_CHECKUP
          : requiredFieldsByStep.FOURTH_DIAGNOSIS;
        break;

      case OccurrenceStepEnum.ANALYSIS_WARRANTY:
        props.requiredFields = isPreventive
          ? requiredFieldsByStep.ANALYSIS_WARRANTY_PREVENTIVE_CHECKUP
          : requiredFieldsByStep.ANALYSIS_WARRANTY;
        break;

      case OccurrenceStepEnum.CUSTOMER_OK:
        props.requiredFields = isPreventive
          ? requiredFieldsByStep.CUSTOMER_OK_PREVENTIVE_CHECKUP
          : requiredFieldsByStep.CUSTOMER_OK;
        break;

      case OccurrenceStepEnum.PARTS:
        props.requiredFields = isPreventive
          ? requiredFieldsByStep.PARTS_PREVENTIVE_CHECKUP
          : requiredFieldsByStep.PARTS;
        break;

      case OccurrenceStepEnum.PARTS_IN_TRANSIT:
        props.requiredFields = isPreventive
          ? requiredFieldsByStep.PARTS_IN_TRANSIT_PREVENTIVE_CHECKUP
          : requiredFieldsByStep.PARTS_IN_TRANSIT;
        break;

      case OccurrenceStepEnum.AWAITING_REPAIR:
        props.requiredFields = isPreventive
          ? requiredFieldsByStep.AWAITING_REPAIR_PREVENTIVE_CHECKUP
          : requiredFieldsByStep.AWAITING_REPAIR;
        break;

      case OccurrenceStepEnum.REPAIR:
        props.requiredFields = isPreventive
          ? requiredFieldsByStep.REPAIR_PREVENTIVE_CHECKUP
          : requiredFieldsByStep.REPAIR;
        break;

      case OccurrenceStepEnum.RELEASE:
        props.requiredFields = isPreventive
          ? requiredFieldsByStep.RELEASE_PREVENTIVE_CHECKUP
          : requiredFieldsByStep.RELEASE;
        break;

      default:
        props.requiredFields = [];
        break;
    }

    if (isDealership) {
      const dealershipExcluded = ['vehicleStopped', 'hasLink', 'mainOccurrence', 'tasNumber', 'tasStatus'];
      props.requiredFields = props.requiredFields.filter(f => !dealershipExcluded.includes(f));
    }

    return <ChangeStepForm reloadOccurrenceList={reloadOccurrenceList} {...props} />;
  };

  const onCloseValidation = () => {
    if (occurrenceFormHasChanged) {
      setShowConfirmDialog(true);
      return;
    } else {
      props.onClose();
    }
  };

  return (
    props.showDialog && (
      <>
        <div className="bg-kanban-sidebar" onClick={() => onCloseValidation()}></div>
        <div className="kanban-sidebar">
          <div className="flex justify-between items-center px-6">
            <div className="text-size-h4 p-4">
              {props.view === 'budgets'
                ? intl.formatMessage({ id: 'intl-msg:kanban.stepFormDialog.budgetsListTitle' })
                : intl.formatMessage({ id: 'intl-msg:kanban.stepFormDialog.occurrenceDataTitle' })}
            </div>

            <div className="flex items-center gap-3 p-4 padding-right-10">
              <span
                className="rioglyph rioglyph-remove text-size-h5 cursor-pointer"
                onClick={() => onCloseValidation()}
              ></span>
            </div>
          </div>
          <hr className="border-color-gray-light border-bottom-solid !mb-0" />
          <div className="flex gap-3">
            <div className="w-[100px] text-center bg-lightest pt-6">
              {!props.summaryStep?.dtEnd && props.persona !== 'MANAGER' && !props.finalized && (
                <div className="p-2 mb-4">
                  <span
                    className={`rioglyph rioglyph-compare text-size-h1 cursor-pointer ${
                      props.view === 'changeStep' ? 'text-color-primary' : 'text-color-gray'
                    }`}
                    onClick={() => props.setView('changeStep')}
                  />
                </div>
              )}
              <div className="p-2 mb-4">
                <span
                  className={`rioglyph rioglyph-clipboard text-size-h1 cursor-pointer ${
                    props.view === 'form' ? 'text-color-primary' : 'text-color-gray'
                  }`}
                  onClick={() => props.setView('form')}
                />
              </div>
              {(userRole === UserRoleEnum.CONSULTANT ||
                  userRole === UserRoleEnum.TOWER ||
                  userRole === UserRoleEnum.MANAGER) && showBudget &&
                  (userRole !== UserRoleEnum.CONSULTANT || showBudgetConsultant) && (
                  <div className="p-2 mb-4 flex items-center justify-center">
                    <img
                      src={
                        props.view === 'budgets'
                          ? '/assets/images/dollar-icon.svg'
                          : '/assets/images/dollar-icon-gray.svg'
                      }
                      alt={intl.formatMessage({ id: 'intl-msg:kanban.stepFormDialog.budgets' })}
                      className="cursor-pointer"
                      style={{ width: 30, height: 30 }}
                      onClick={() => props.setView('budgets')}
                    />
                  </div>
                )}
              {props.persona !== 'MANAGER' && (
                <div className="p-2 mb-4">
                  <span
                    className={`rioglyph rioglyph-time text-size-h1 cursor-pointer ${
                      props.view === 'worklog' ? 'text-color-primary' : 'text-color-gray'
                    }`}
                    onClick={() => props.setView('worklog')}
                  />
                </div>
              )}

              {props.persona === 'MANAGER' &&
                props.occurrence?.steps?.find((step) => step.step === props.step)?.dtEnd && (
                  <div className="p-2 mb-4">
                    <span
                      className={`rioglyph rioglyph-comment text-size-h1 cursor-pointer ${
                        props.view === 'comments' ? 'text-color-primary' : 'text-color-gray'
                      }`}
                      onClick={() => props.setView('comments')}
                    />
                  </div>
                )}

              <div className="p-2 mb-4">
                <span
                  className={`rioglyph rioglyph-support text-size-h1 cursor-pointer ${
                    props.view === 'support' ? 'text-color-primary' : 'text-color-gray'
                  }`}
                  onClick={() => props.setView('support')}
                />
              </div>
            </div>
            <div className="w-full py-6 px-6 overflow-y-auto h-[calc(100vh-50px)] !pb-[50px]">
              {occurrence ? (
                props.view === 'form' ? (
                  form()
                ) : props.view === 'changeStep' ? (
                  changeStep()
                ) : props.view === 'comments' ? (
                  <AssessmentForm
                    occurrenceId={occurrence.id}
                    defaultStep={props.step}
                    review={props.review}
                    reviewStep={props.reviewStep}
                    steps={occurrence.steps ?? []}
                  />
                ) : props.view === 'worklog' ? (
                  <Worklog occurrenceId={occurrence.id} />
                ) : props.view === 'budgets' ? (
                  <BudgetsForm
                    currentStep={props.step}
                    occurrenceId={occurrence.id}
                    onViewAttachments={() => props.setView('form')}
                  />
                ) : props.view === 'support' ? (
                  <SupportTab occurrence={occurrence} persona={props.persona} />
                ) : null
              ) : (
                <div className="flex items-center justify-center text-center h-full">
                  <div className="flex">
                    <div className="spinner mr-4">
                      <div />
                      <div />
                      <div />
                      <div />
                      <div />
                      <div />
                      <div />
                      <div />
                    </div>
                    {intl.formatMessage({ id: 'intl-msg:kanban.stepFormDialog.loadingOccurrence' })}
                  </div>
                </div>
              )}
            </div>
          </div>

          <ConfirmDialog
            show={showConfirmDialog}
            title={intl.formatMessage({ id: 'intl-msg:kanban.stepFormDialog.confirmExitTitle' })}
            body={intl.formatMessage({ id: 'intl-msg:kanban.stepFormDialog.confirmExitBody' })}
            onClose={() => setShowConfirmDialog(false)}
            onConfirm={() => {
              setOccurrence(occurrenceInit ? { ...occurrenceInit } : ({} as Occurrence));
              props.onClose();
              setShowConfirmDialog(false);
            }}
          />
        </div>
      </>
    )
  );
};

export default StepFormDialog;
